import { useState } from 'react';
import { Link } from 'react-router-dom';
import { formatCount, formatPercent, formatCents } from '@affily/shared/money';
import { useFetch } from '../../hooks/useFetch.js';
import { DashboardLayout } from '../../components/DashboardLayout.jsx';
import { TermsInline } from '../../components/Terms.jsx';
import { Button, Badge, Spinner, EmptyState, Unknown } from '../../components/ui.jsx';

const statusTone = { active: 'positive', paused: 'caution', draft: 'neutral', closed: 'neutral' };

export default function BrandPrograms() {
  const { data, loading } = useFetch('/programs/mine/list');
  const programs = data?.programs ?? [];

  return (
    <DashboardLayout
      side="brand"
      title="Programs"
      description="A public program’s terms are open to everyone the moment it is active; a private one’s go only to the creators you invite or accept."
      actions={
        <Button to="/dashboard/brand/programs/new" size="sm">
          New program
        </Button>
      }
    >
      {loading ? (
        <Spinner />
      ) : programs.length === 0 ? (
        <EmptyState
          title="No programs yet"
          action={<Button to="/dashboard/brand/programs/new">List your first program</Button>}
        >
          A program publishes your commission rate, cookie window and payout schedule so creators
          can decide without an application gate.
        </EmptyState>
      ) : (
        <div className="overflow-x-auto rounded-lg border border-rule bg-paper-raised">
          <table className="data-table">
            <thead>
              <tr>
                <th>Program</th>
                <th>Terms</th>
                <th className="num">Partners</th>
                <th className="num">Clicks</th>
                <th className="num">Conv.</th>
                <th className="num">CVR</th>
                <th className="num">Commission</th>
                <th />
              </tr>
            </thead>
            <tbody>
              {programs.map((p) => (
                <tr key={p.id}>
                  <td>
                    <Link
                      to={`/dashboard/brand/programs/${p.id}`}
                      className="font-medium text-ink hover:text-accent"
                    >
                      {p.title}
                    </Link>
                    <div className="mt-0.5 flex items-center gap-1.5">
                      <Badge tone={statusTone[p.status]}>{p.status}</Badge>
                      {p.isBoosted && <Badge tone="accent">Boosted</Badge>}
                      {p.visibility === 'private' && <Badge>Private</Badge>}
                    </div>
                  </td>
                  <td>
                    <TermsInline program={p} />
                  </td>
                  <td className="num">{formatCount(p.performance.activePartnerships)}</td>
                  <td className="num">{formatCount(p.performance.uniqueClicks)}</td>
                  <td className="num">{formatCount(p.performance.conversions)}</td>
                  <td className="num">
                    {p.performance.conversionRatePct === null ? (
                      <Unknown reason="No clicks yet" />
                    ) : (
                      formatPercent(p.performance.conversionRatePct)
                    )}
                  </td>
                  <td className="num">{formatCents(p.performance.commissionGrossCents, { currency: p.currency })}</td>
                  <td className="num whitespace-nowrap">
                    <Link
                      to={`/dashboard/brand/programs/${p.id}`}
                      className="mr-3 text-xs font-medium text-accent hover:underline"
                    >
                      Edit
                    </Link>
                    <Link
                      to={`/dashboard/brand/programs/${p.id}/actions`}
                      className="mr-3 text-xs font-medium text-accent hover:underline"
                    >
                      Tracking
                    </Link>
                    {p.competition && (
                      <Link
                        to={`/dashboard/brand/programs/${p.id}/competition`}
                        className="mr-3 text-xs font-medium text-accent hover:underline"
                      >
                        Competition
                      </Link>
                    )}
                    {/* A draft has no public page; anyone but its owner gets a 404. */}
                    {p.status === 'draft' ? (
                      <span className="text-xs text-ink-faint">Not public until active</span>
                    ) : (
                      <>
                        <Link
                          to={`/programs/${p.slug}`}
                          className="text-xs text-ink-muted hover:text-accent"
                        >
                          {p.visibility === 'private' ? 'View page' : 'View public'}
                        </Link>
                        {p.visibility === 'private' && <CopyLink slug={p.slug} />}
                      </>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </DashboardLayout>
  );
}

/** A private program is shared by its link: this puts it on the clipboard. */
function CopyLink({ slug }) {
  const [copied, setCopied] = useState(false);
  const url = `${window.location.origin}/programs/${slug}`;
  async function copy() {
    try {
      await navigator.clipboard.writeText(url);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      window.prompt('Copy this link', url);
    }
  }
  return (
    <button type="button" onClick={copy} className="ml-3 text-xs font-medium text-accent hover:underline">
      {copied ? 'Copied' : 'Copy link'}
    </button>
  );
}
